import React, { useState, useEffect } from "react";
import MindMap from "./MindMap";
import Coach from "./Coach";
import Insights from "./Insights";
import VetosView from "./VetosView";
import BlurbsView from "./BlurbsView";
import FrameworkHelp from "./FrameworksHelp"; 
import { Button } from "react-bootstrap";
import { AnimatePresence, motion } from "framer-motion";
import "./Frameworks.css";
import SeeTabsDashboard from "./SeeTabsDashboard"; 
import OnboardingQuestionsModal from "./onboardingQuestions"; 
import api from "../api.js";
import VideoPlayer from "../components/VideoPlayer";
import WelcomeVideoModal from "../components/WelcomeVideoModal";


// User-namespaced keys for all frameworks
const getNamespacedKey = (key) => {
  const userId = localStorage.getItem("user_id");
  return userId ? `${key}_${userId}` : key;
};
const MIND_MAP_STATE_KEY      = getNamespacedKey("frameworkState");
const MIND_MAP_PICK_FORM_KEY  = getNamespacedKey("frameworkPickFormData");
const MIND_MAP_ASK_NODE_KEY   = getNamespacedKey("askNodeCache");
const COACH_STATE_KEY         = getNamespacedKey("coachState");
const INSIGHTS_LOG_KEY        = getNamespacedKey("insightsLog");
const VETOS_STATE_KEY         = getNamespacedKey("vetosState");
const BLURBS_STATE_KEY        = getNamespacedKey("blurbsState");

// // Guidance text for each view (full text preserved)
// const guidanceMapping = {
//   "Mind Map": "Click on a tile to get started. You can look at your thought through multiple angles by exploring different branches, or you can start a new thought if you want to start over. Golden tiles will call on Vybly to help give you insights. Each golden tile does something different. You can change your answers by clicking a tile again.",
//   "Coach": "Use the Coach to simulate scenarios or role-play situations. The Coach will run you through six scenarios. For each scenario, you will have six answers. You can select the answer in front of you or go to the next one, but you cannot go back! Pick whichever answer feels most like you, and get ready for unique insights.",
//   "Insights": "The Insights log helps you keep track of anything that matters to you: feelings, habits, goals... Every time you log five new entries, Vybly will help you figure out patterns in your life. You can also use this to keep track of your progress and see how you are doing over time. Just fill out the form and click 'Add Entry' below.",
// };

const Frameworks = () => {
  const [viewMode, setViewMode] = useState("Mind Map");
  const [coachKey, setCoachKey] = useState(0);
  const [insightsKey, setInsightsKey] = useState(0);
  const [vetosKey, setVetosKey] = useState(0);
  const [blurbsKey, setBlurbsKey] = useState(0);
  const [vetosResult, setVetosResult] = useState(null);
  const [vetosStep, setVetosStep] = useState(0);
  const [blurbsStep, setBlurbsStep] = useState(0);

  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false);
  const [hasCheckedIntro, setHasCheckedIntro] = useState(false);

  useEffect(() => {
  async function checkIntroSeen() {
    try {
      const userId = localStorage.getItem("user_id");
      if (!userId) return;
      const res = await api.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/user/intro_status/?user_id=${userId}`
      );
      console.log("Fetched intro status:", res.data);
      const seenIntro = res.data?.has_seen_intro_video;
      // Handle all falsy/string/number cases
      const isUnseen =
        seenIntro === false ||
        seenIntro === null ||
        seenIntro === undefined ||
        seenIntro === "f" ||
        seenIntro === "false" ||
        seenIntro === "False" ||
        seenIntro === 0;
      if (isUnseen) {
        setShowWelcomeVideo(true);
      }
    } catch (e) {
      console.error("Intro status fetch error:", e);
    } finally {
      setHasCheckedIntro(true);
    }
  }
  checkIntroSeen();
}, []);

  const handleWelcomeVideoClose = async () => {
    setShowWelcomeVideo(false);
    try {
      const userId = localStorage.getItem("user_id");
      if (!userId) return;
      await api.post(`${process.env.REACT_APP_API_BASE_URL}/api/user/set_intro_seen`, { user_id: userId });
    } catch (e) {
      // Optionally log error
    }
  };

  // RESET HANDLERS (unchanged)
  const handleResetMindMap = () => {
    localStorage.removeItem(MIND_MAP_STATE_KEY);
    localStorage.removeItem(MIND_MAP_PICK_FORM_KEY);
    localStorage.removeItem(MIND_MAP_ASK_NODE_KEY);
  };
  const handleResetCoach = () => {
    localStorage.removeItem(COACH_STATE_KEY);
    setCoachKey((k) => k + 1);
  };
  const handleResetVetos = () => {
    localStorage.removeItem(VETOS_STATE_KEY);
    setVetosKey((k) => k + 1);
    setVetosResult(null);
  };
  const handleResetBlurbs = () => {
    localStorage.removeItem(BLURBS_STATE_KEY);
    setBlurbsKey((k) => k + 1);
  };
  const handleResetInsights = () => {
    localStorage.removeItem(INSIGHTS_LOG_KEY);
    setInsightsKey((k) => k + 1);
  };

  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const checkOnboarding = async () => {
      try {
        const token = localStorage.getItem("token");
          const res = await api.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/user/questionsAnswered`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        if (!res.data.allAnswered) {
          setShowOnboarding(true);
        }
      } catch (e) {
        // Optionally handle/log error
      }
    };
    checkOnboarding();
  }, []);


  return (

    <>
          {showWelcomeVideo && (
            <WelcomeVideoModal
              src={process.env.REACT_APP_ONBOARDING_VIDEO_URL}
              poster="/images/welcome-poster.jpg"
              onClose={handleWelcomeVideoClose}
            />
          )}
    
    <div className="frameworks-container" style={{ position: "relative" }}>
      {/* FrameworkHelp is rendered here */}
      <FrameworkHelp framework={viewMode} visible={true} />

      <div className="framework-tabs-row">
      <div className="framework-tabs-bar">
        <Button variant={viewMode === "Mind Map" ? "primary" : "outline-primary"} onClick={() => setViewMode("Mind Map")}>Mind Map</Button>
        <Button variant={viewMode === "Coach" ? "primary" : "outline-primary"} onClick={() => setViewMode("Coach")}>Coach</Button>
        <Button variant={viewMode === "Insights" ? "primary" : "outline-primary"} onClick={() => setViewMode("Insights")}>Insights</Button>
        <Button variant={viewMode === "Vetos" ? "primary" : "outline-primary"} onClick={() => setViewMode("Vetos")}>Vetos</Button>
        <Button variant={viewMode === "Blurbs" ? "primary" : "outline-primary"} onClick={() => setViewMode("Blurbs")}>Blurbs</Button>
      </div>
      <button
        className="see-tabs-btn"
        onClick={() => setViewMode("SeeTabs")}
        onMouseOver={e => {
          e.currentTarget.style.background = "#fff1b8";
          e.currentTarget.style.boxShadow = "0 4px 16px #ffd96699";
          e.currentTarget.style.borderColor = "#ffc53d";
        }}
        onMouseOut={e => {
          e.currentTarget.style.background = "#fffbe6";
          e.currentTarget.style.boxShadow = "0 2px 8px #ffd96655";
          e.currentTarget.style.borderColor = "#ffd966";
        }}
      >
        See Tabs
      </button>
    </div>

      {/* Animated guidance text
      <div className="guidance-container">
        <AnimatePresence exitBeforeEnter>
          <motion.p
            key={viewMode}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.5 }}
            className="guidance-text"
          >
            {guidanceMapping[viewMode]}
          </motion.p>
        </AnimatePresence>
      </div> */}

      {/* Disclaimer: Only show for non-Insights views */}
      {viewMode !== "Insights" && (
        <div className="header" style={{ textAlign: "center", marginBottom: "1rem" }}>
          <div className="disclaimer">
            For data privacy, all data is only stored on this device. For brainstorming only. Vybly can make mistakes.
          </div>
        </div>
      )}

      {/* Main Container */}
      <div id="frameworksMainContainer" style={{ margin: "0 auto", width: "100%"}}>
        {viewMode === "Mind Map" && <MindMap />}
        {viewMode === "Coach" && <Coach key={coachKey} />}
        {viewMode === "Insights" && <Insights key={insightsKey} active={viewMode === "Insights"} />}
        {viewMode === "Vetos" && (<VetosView key={vetosKey} onStepChange={setVetosStep} />)}
        {viewMode === "Blurbs" && (<BlurbsView key={blurbsKey} onStepChange={setBlurbsStep} />)}
        {viewMode === "SeeTabs" && <SeeTabsDashboard />}
      </div>

      {/* Reset/Start new buttons - DO NOT REMOVE ANY BUTTON LOGIC HERE */}
      <div style={{ margin: "1rem auto", textAlign: "center" }}>
        {/* {viewMode === "Mind Map" && (
          <Button variant="secondary" onClick={handleResetMindMap}>
            Start new thoughts
          </Button>
        )} */}
        {viewMode === "Coach" && (
          <Button variant="secondary" onClick={handleResetCoach}>
            Start new scenario
          </Button>
        )}
        {viewMode === "Insights" && (
          <Button variant="secondary" onClick={handleResetInsights}>
            Delete log permanently
          </Button>
        )}
        {viewMode === "Vetos" && (
          vetosStep === 4 ? null : (
            <Button variant="secondary" onClick={() => {
              setVetosKey(vetosKey + 1);
              setVetosResult(null);
              handleResetVetos();
            }}>
              Start new Vetos
            </Button>
          )
        )}
        {viewMode === "Blurbs" && (
          blurbsStep !== 0 ? null : (
            <Button variant="secondary" onClick={handleResetBlurbs}>
              Start new Blurbs
            </Button>
          )
        )}
      </div>
      {showOnboarding && (
        <OnboardingQuestionsModal
          onClose={() => setShowOnboarding(false)}
        />
      )}
    </div>
    </>
  );
};

export default Frameworks;
